import { Edit, Eye, MoreVertical, Plus, Shield, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/DropdownMenu';
import { Input } from '../components/ui/Input';
import { Pagination } from '../components/ui/Pagination';
import { Skeleton } from '../components/ui/Skeleton';
import { Table, TableColumn } from '../components/ui/Table';
import useLocale from '../contexts/LocaleContext';
import { getCurrentUser } from '../hooks/useAuth';
import { useFacilityTypes } from '../hooks/useFacilities';
import {
  useCreateRole,
  useDeleteRole,
  usePermissions,
  useRoles,
  useUpdateRole,
} from '../hooks/useRoles';
import { hasPermission } from '../lib/permissions';
import type { RoleDto } from '../types/api';

export default function Roles() {
  const { dictionary } = useLocale();
  const currentUser = getCurrentUser();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState<RoleDto | null>(null);
  const [viewRole, setViewRole] = useState<RoleDto | null>(null);
  const [deleteRole, setDeleteRole] = useState<RoleDto | null>(null);

  const { data: rolesResponse, isLoading } = useRoles({ page, limit });
  const { data: permissions = [], isLoading: permissionsLoading } = usePermissions();
  const { data: facilityTypes = [] } = useFacilityTypes();
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const deleteMutation = useDeleteRole();

  const roles = rolesResponse?.data ?? [];
  const pagination = rolesResponse?.pagination;
  const totalItems = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const getRolePermissionIds = (role: RoleDto): string[] =>
    role.permissionIds ?? role.permissions?.map(p => p.id) ?? [];

  const getRoleFacilityTypeIds = (role: RoleDto): string[] =>
    role.facilityTypeIds ?? role.facilityTypes?.map(ft => ft.id) ?? [];

  const getRolePermissionLabels = (role: RoleDto): string[] => {
    if (role.permissions?.length) {
      return role.permissions.map(p => p.name || p.code || p.id);
    }
    const idSet = new Set(getRolePermissionIds(role));
    return permissions
      .filter(p => idSet.has(p.id))
      .map(p => p.name || p.code || p.id);
  };

  const getRoleFacilityTypeLabels = (role: RoleDto): string[] => {
    if (role.facilityTypes?.length) {
      return role.facilityTypes.map(ft => ft.name || ft.code || ft.id);
    }
    const idSet = new Set(getRoleFacilityTypeIds(role));
    return facilityTypes
      .filter(ft => idSet.has(ft.id))
      .map(ft => ft.name || ft.code || ft.id);
  };

  if (!hasPermission(currentUser as any, 'ROLES_MANAGEMENT')) {
    return (
      <div className='p-6'>
        <div className='bg-red-50 border-l-4 border-red-500 p-6 rounded-lg'>
          <p className='text-red-600'>{dictionary['roles.access_denied'] || 'You do not have permission to manage roles.'}</p>
        </div>
      </div>
    );
  }

  const columns: TableColumn<RoleDto>[] = [
    {
      key: 'name',
      label: dictionary['roles.name'] || 'Name',
      sortable: true,
      render: row => (
        <div className='flex items-center gap-2'>
          <span className='font-medium text-foreground'>{row.name}</span>
          {row.isSystem && (
            <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-secondary text-primary'>
              <Shield className='h-3 w-3' />
              {dictionary['roles.system'] || 'System'}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'code',
      label: dictionary['roles.code'] || 'Code',
      sortable: true,
      render: row => <span className='text-sm text-muted-foreground font-mono'>{row.code}</span>,
    },
    {
      key: 'description',
      label: dictionary['roles.description'] || 'Description',
      render: row => (
        <span className='text-sm text-muted-foreground line-clamp-2'>{row.description || '—'}</span>
      ),
    },
    {
      key: 'permissionIds',
      label: dictionary['roles.permissions'] || 'Permissions',
      render: row => (
        <span className='text-sm text-muted-foreground'>
          {row.permissionIds?.length ?? row.permissions?.length ?? 0}
        </span>
      ),
    },
    {
      key: 'facilityTypeIds',
      label: dictionary['roles.facility_types'] || 'Facility types',
      render: row => (
        <span className='text-sm text-muted-foreground'>
          {row.facilityTypeIds?.length ?? row.facilityTypes?.length ?? 0}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: row => (
        <div className='flex justify-end'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='outline' size='sm' className='h-8 px-2'>
                <MoreVertical className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              <DropdownMenuItem
                className='gap-2'
                onClick={() => setViewRole(row)}
              >
                <Eye className='h-4 w-4' />
                {dictionary['common.view'] || 'View details'}
              </DropdownMenuItem>
              <DropdownMenuItem
                className='gap-2'
                onClick={() => setEditRole(row)}
              >
                <Edit className='h-4 w-4' />
                {dictionary['common.edit'] || 'Edit'}
              </DropdownMenuItem>
              {!row.isSystem && (
                <DropdownMenuItem
                  variant='destructive'
                  className='gap-2'
                  onClick={() => setDeleteRole(row)}
                >
                  <Trash2 className='h-4 w-4' />
                  {dictionary['common.delete'] || 'Delete'}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className='p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-[1600px] mx-auto'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
          <div>
            <h1 className='text-lg sm:text-xl lg:text-2xl font-bold text-foreground'>
              {dictionary['roles.title'] || 'Roles & Permissions'}
            </h1>
            <p className='text-xs sm:text-sm text-muted-foreground mt-1'>
              {dictionary['roles.subtitle'] || 'Manage roles and their permissions and facility type access.'}
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className='gap-2'>
            <Plus className='h-4 w-4' />
            {dictionary['roles.create_role'] || 'Create role'}
          </Button>
        </div>

        <div className='bg-card border border-border rounded-xl overflow-hidden'>
          {isLoading ? (
            <div className='p-6 space-y-3'>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className='h-12 w-full' />
              ))}
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                data={roles}
                isLoading={false}
                enableSearch={false}
                enablePagination={false}
                emptyMessage={dictionary['roles.no_roles'] || 'No roles yet. Create one to get started.'}
              />
              {totalItems > 0 && (
                <div className='border-t border-border px-4 py-3'>
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    itemsPerPage={limit}
                    onPageChange={setPage}
                    onItemsPerPageChange={l => {
                      setLimit(l);
                      setPage(1);
                    }}
                    showItemsPerPage
                    itemsPerPageOptions={[10, 20, 50]}
                    translations={{
                      showing: dictionary['common.showing'],
                      to: dictionary['common.to'],
                      of: dictionary['common.of'],
                      results: dictionary['common.results'],
                      itemsPerPage: dictionary['common.items_per_page'],
                      previous: dictionary['common.previous'],
                      next: dictionary['common.next'],
                      firstPage: dictionary['common.first_page'],
                      lastPage: dictionary['common.last_page'],
                      goToPage: dictionary['common.go_to_page'],
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <RoleFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        permissions={permissions}
        facilityTypes={facilityTypes}
        permissionsLoading={permissionsLoading}
        onSubmit={async (values) => {
          await createMutation.mutateAsync(values);
          setCreateOpen(false);
        }}
        isLoading={createMutation.isPending}
        mode='create'
        dictionary={dictionary}
      />

      {editRole && (
        <RoleFormDialog
          open={!!editRole}
          onOpenChange={open => !open && setEditRole(null)}
          role={editRole}
          permissions={permissions}
          facilityTypes={facilityTypes}
          permissionsLoading={permissionsLoading}
          onSubmit={async (values) => {
            await updateMutation.mutateAsync({
              id: editRole.id,
              data: {
                name: values.name,
                description: values.description,
                permissionIds: values.permissionIds,
                facilityTypeIds: values.facilityTypeIds,
              },
            });
            setEditRole(null);
          }}
          isLoading={updateMutation.isPending}
          mode='edit'
          dictionary={dictionary}
        />
      )}

      {viewRole && (
        <Dialog open={!!viewRole} onOpenChange={open => !open && setViewRole(null)}>
          <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
            <DialogHeader>
              <DialogTitle>{dictionary['common.view'] || 'View details'}</DialogTitle>
              <DialogDescription>
                {dictionary['roles.details_description'] || 'Role information, permissions and facility types.'}
              </DialogDescription>
            </DialogHeader>

            <div className='space-y-4'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm'>
                <div>
                  <p className='text-muted-foreground'>{dictionary['roles.name'] || 'Name'}</p>
                  <p className='font-medium'>{viewRole.name}</p>
                </div>
                <div>
                  <p className='text-muted-foreground'>{dictionary['roles.code'] || 'Code'}</p>
                  <p className='font-mono'>{viewRole.code}</p>
                </div>
              </div>

              <div>
                <p className='text-muted-foreground text-sm'>{dictionary['roles.description'] || 'Description'}</p>
                <p className='text-sm'>{viewRole.description || '—'}</p>
              </div>

              <div>
                <p className='font-medium text-sm mb-2'>
                  {dictionary['roles.permissions'] || 'Permissions'} ({getRolePermissionIds(viewRole).length})
                </p>
                <div className='max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1'>
                  {getRolePermissionLabels(viewRole).length === 0 ? (
                    <p className='text-sm text-muted-foreground'>{dictionary['roles.no_permissions'] || 'No permissions assigned'}</p>
                  ) : (
                    getRolePermissionLabels(viewRole).map(label => (
                      <div key={label} className='text-sm px-2 py-1 rounded bg-muted/40'>
                        {label}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div>
                <p className='font-medium text-sm mb-2'>
                  {dictionary['roles.facility_types'] || 'Facility types'} ({getRoleFacilityTypeIds(viewRole).length})
                </p>
                <div className='max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1'>
                  {getRoleFacilityTypeLabels(viewRole).length === 0 ? (
                    <p className='text-sm text-muted-foreground'>{dictionary['roles.no_facility_types'] || 'No facility types assigned'}</p>
                  ) : (
                    getRoleFacilityTypeLabels(viewRole).map(label => (
                      <div key={label} className='text-sm px-2 py-1 rounded bg-muted/40'>
                        {label}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => setViewRole(null)}>
                {dictionary['common.close'] || 'Close'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog
        open={!!deleteRole}
        onOpenChange={open => !open && setDeleteRole(null)}
        onConfirm={async () => {
          if (deleteRole) {
            await deleteMutation.mutateAsync(deleteRole.id);
            setDeleteRole(null);
          }
        }}
        title={dictionary['roles.delete_confirm_title'] || 'Delete role'}
        description={
          deleteRole
            ? (dictionary['roles.delete_confirm_description'] || 'Are you sure you want to delete this role? This action cannot be undone.').replace('{name}', deleteRole.name)
            : ''
        }
        confirmText={dictionary['common.delete'] || 'Delete'}
        variant='danger'
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}

interface RoleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: RoleDto;
  permissions: { id: string; code: string; name: string; description: string }[];
  facilityTypes: { id: string; name: string; code?: string }[];
  permissionsLoading: boolean;
  onSubmit: (values: {
    name: string;
    code: string;
    description: string;
    permissionIds: string[];
    facilityTypeIds: string[];
  }) => Promise<void>;
  isLoading: boolean;
  mode: 'create' | 'edit';
  dictionary: Record<string, string>;
}

function RoleFormDialog({
  open,
  onOpenChange,
  role,
  permissions,
  facilityTypes,
  permissionsLoading,
  onSubmit,
  isLoading,
  mode,
  dictionary,
}: RoleFormDialogProps) {
  const [name, setName] = useState(role?.name ?? '');
  const [code, setCode] = useState(role?.code ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [permissionIds, setPermissionIds] = useState<string[]>(
    role?.permissionIds ?? role?.permissions?.map(p => p.id) ?? []
  );
  const [facilityTypeIds, setFacilityTypeIds] = useState<string[]>(
    role?.facilityTypeIds ?? role?.facilityTypes?.map(ft => ft.id) ?? []
  );
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      const initialPermissionIds = role?.permissionIds ?? role?.permissions?.map(p => p.id) ?? [];
      const initialFacilityTypeIds = role?.facilityTypeIds ?? role?.facilityTypes?.map(ft => ft.id) ?? [];
      setName(role?.name ?? '');
      setCode(role?.code ?? '');
      setDescription(role?.description ?? '');
      setPermissionIds(initialPermissionIds);
      setFacilityTypeIds(initialFacilityTypeIds);
      setError('');
    }
  }, [open, role]);

  const reset = () => {
    const initialPermissionIds = role?.permissionIds ?? role?.permissions?.map(p => p.id) ?? [];
    const initialFacilityTypeIds = role?.facilityTypeIds ?? role?.facilityTypes?.map(ft => ft.id) ?? [];
    setName(role?.name ?? '');
    setCode(role?.code ?? '');
    setDescription(role?.description ?? '');
    setPermissionIds(initialPermissionIds);
    setFacilityTypeIds(initialFacilityTypeIds);
    setError('');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const togglePermission = (id: string) => {
    setPermissionIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleFacilityType = (id: string) => {
    setFacilityTypeIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError(dictionary['roles.name_required'] || 'Name is required');
      return;
    }
    if (mode === 'create' && !code.trim()) {
      setError(dictionary['roles.code_required'] || 'Code is required');
      return;
    }
    try {
      await onSubmit({
        name: name.trim(),
        code: code.trim() || role!.code,
        description: description.trim(),
        permissionIds,
        facilityTypeIds,
      });
      handleOpenChange(false);
    } catch {
      setError(dictionary['roles.save_error'] || 'Failed to save role');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-w-lg max-h-[90vh] overflow-y-auto' showCloseButton={!isLoading}>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? (dictionary['roles.create_role'] || 'Create role')
              : (dictionary['roles.edit_role'] || 'Edit role')}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? (dictionary['roles.create_role_description'] || 'Add a new role with permissions and facility type access.')
              : (dictionary['roles.edit_role_description'] || 'Update role name, description, permissions and facility types.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label className='text-sm font-medium text-foreground'>{dictionary['roles.name'] || 'Name'}</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={dictionary['roles.name_placeholder'] || 'e.g. District Manager'}
              className='mt-1'
            />
          </div>
          {mode === 'create' && (
            <div>
              <label className='text-sm font-medium text-foreground'>{dictionary['roles.code'] || 'Code'}</label>
              <Input
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase().replace(/\s/g, '_'))}
                placeholder={dictionary['roles.code_placeholder'] || 'e.g. CUSTOM_DISTRICT'}
                className='mt-1 font-mono'
              />
            </div>
          )}
          <div>
            <label className='text-sm font-medium text-foreground'>{dictionary['roles.description'] || 'Description'}</label>
            <Input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={dictionary['roles.description_placeholder'] || 'Optional description'}
              className='mt-1'
            />
          </div>

          <div>
            <label className='text-sm font-medium text-foreground'>{dictionary['roles.permissions'] || 'Permissions'}</label>
            {permissionsLoading ? (
              <div className='mt-2 text-sm text-muted-foreground'>{dictionary['common.loading'] || 'Loading...'}</div>
            ) : (
              <div className='mt-2 max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1'>
                {permissions.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>{dictionary['roles.no_permissions'] || 'No permissions available'}</p>
                ) : (
                  permissions.map(p => (
                    <label key={p.id} className='flex items-center gap-2 py-1.5 cursor-pointer hover:bg-muted/50 rounded px-2'>
                      <input
                        type='checkbox'
                        checked={permissionIds.includes(p.id)}
                        onChange={() => togglePermission(p.id)}
                        className='rounded border-border'
                      />
                      <span className='text-sm'>{p.name || p.code}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          <div>
            <label className='text-sm font-medium text-foreground'>{dictionary['roles.facility_types'] || 'Facility types'}</label>
            <div className='mt-2 max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1'>
              {facilityTypes.length === 0 ? (
                <p className='text-sm text-muted-foreground'>{dictionary['roles.no_facility_types'] || 'No facility types available'}</p>
              ) : (
                facilityTypes.map(ft => (
                  <label key={ft.id} className='flex items-center gap-2 py-1.5 cursor-pointer hover:bg-muted/50 rounded px-2'>
                    <input
                      type='checkbox'
                      checked={facilityTypeIds.includes(ft.id)}
                      onChange={() => toggleFacilityType(ft.id)}
                      className='rounded border-border'
                    />
                    <span className='text-sm'>{ft.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {error && (
            <p className='text-sm text-red-600'>{error}</p>
          )}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => handleOpenChange(false)} disabled={isLoading}>
              {dictionary['common.cancel'] || 'Cancel'}
            </Button>
            <Button type='submit' disabled={isLoading}>
              {isLoading ? (dictionary['common.saving'] || 'Saving...') : (dictionary['common.save'] || 'Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
